import { Address } from '@adornis/address/db/address.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { countingTableView } from '@nsc/simple-lms/client/record-count-view.js';
import { ErmValue } from '@adornis/entity-relationship-management/client/details/erm-value.js';
import type { ERMEntityDefinition } from '@adornis/entity-relationship-management/client/types.js';
import { html, nothing } from 'lit';
import { countScopedPartnerCompanies, subscribeScopedPartnerCompaniesPaginated } from '../../api/scoped-queries.js';
import { softDeletePartnerCompany } from '../../api/soft-delete.js';
import { SmartTeamsCompany } from '../../db/index.js';
import { FieldEngineerSync } from '../components/field-engineer-sync.js';
import { LinkedPartnerManagers } from '../components/linked-partner-managers.js';
import { ServiceDeskEmailSettings } from '../components/service-desk-email-settings.js';
import { ServiceNowClientPicker } from '../components/servicenow-pickers.js';
import { showCreateCompanyDialog } from '../dialogs/index.js';
import { confirmAndSoftDelete } from './soft-delete.js';

/**
 * Built per user: the service-desk email section is for NSC Admin only (ServiceDeskSettingsManage), so
 * both its visibility and the fields it loads depend on who is looking. Everyone else gets a form
 * without it and never requests the addresses. The field engineer sync section follows the FE Import / Export
 * permission, the same one the sync itself checks on the server.
 */
export const createPartnerCompanyManagerConfig = ({
  canManageServiceDesk,
  canSyncFieldEngineers,
}: {
  canManageServiceDesk: boolean;
  canSyncFieldEngineers: boolean;
}): ERMEntityDefinition<typeof SmartTeamsCompany> => ({
  _class: SmartTeamsCompany,
  label: 'Partner Companies',
  icon: 'icon-[mdi--office-building-outline]',
  route: 'partner-companies',
  detail: {
    operation: id => getByID(SmartTeamsCompany, id),
    selectionSet: {
      _id: 1,
      name: 1,
      address: selectionSet(() => Address, 3),
      serviceNowClientId: 1,
      archivedAt: 1,
      ...(canManageServiceDesk ? { serviceDeskTo: 1, serviceDeskCc: 1 } : {}),
    },
    title: e => e.name ?? 'New partner company',
    sections: [
      {
        title: 'Partner company information',
        fields: [
          { fieldName: 'name', label: 'Name' },
          { fieldName: 'address', label: 'Address' },
          {
            fieldName: 'serviceNowClientId',
            label: 'ServiceNow company',
            // The section is a grid of narrow cells; the label (name, code and id) is long, so this field takes the
            // whole row.
            render(props) {
              return html`<div class="col-span-full">
                ${ErmValue(
                  'ServiceNow company',
                  'sync',
                  ServiceNowClientPicker({
                    ...props.form.field('serviceNowClientId'),
                    placeholder: 'Search ServiceNow company…',
                    showId: true,
                    parentsOnly: true,
                  }),
                )}
                <p class="mt-1 text-xs text-base-content/60">
                  Only parent companies are shown. They are used to identify this company's field engineers in
                  ServiceNow.
                </p>
              </div>`;
            },
          },
        ],
      },
      { title: 'Partner managers', render: ({ entity }) => LinkedPartnerManagers({ companyId: entity._id }) },
      {
        title: 'Field engineers from ServiceNow',
        canAccess: () => canSyncFieldEngineers,
        render: ({ entity }) =>
          FieldEngineerSync({ companyId: entity?._id, clientLinked: Boolean(entity?.serviceNowClientId) }),
      },
      {
        title: 'Service desk email',
        canAccess: () => canManageServiceDesk,
        render: ({ entity }) =>
          entity
            ? ServiceDeskEmailSettings({
                to: entity.serviceDeskTo ?? '',
                cc: entity.serviceDeskCc ?? [],
                onChange: ({ to, cc }) => {
                  entity.serviceDeskTo = to;
                  entity.serviceDeskCc = cc;
                },
              })
            : nothing,
      },
    ],
    onDelete: entity =>
      confirmAndSoftDelete({
        entityLabel: 'partner company',
        name: entity.name,
        run: () => softDeletePartnerCompany(entity._id)(),
      }),
  },
  list: {
    operation: subscribeScopedPartnerCompaniesPaginated,
    selectionSet: { _id: 1, name: 1, archivedAt: 1 },

    columns: [
      {
        fieldName: 'name',
        columnName: 'Partner company',
        isAlwaysVisible: true,
        selectionSet: { name: 1, address: { zipCity: { zip: 1, city: 1 }, country: 1 } },
        renderItem: company => {
          const addressLine = [
            [company.address?.zipCity?.zip, company.address?.zipCity?.city].filter(Boolean).join(' '),
            company.address?.country,
          ]
            .filter(Boolean)
            .join(', ');
          return html`
            <div class="flex flex-col py-2 px-4">
              <span class="font-medium">${company.name ?? 'Unnamed'}</span>
              ${addressLine
                ? html`<span class="text-xs text-neutral-500">${addressLine}</span>`
                : html`<span class="text-xs italic text-neutral-400">No address</span>`}
            </div>
          `;
        },
      },
    ],
    searchFields: ['name', 'address.zipCity.city', 'address.country'],
    listViews: [countingTableView(filter => countScopedPartnerCompanies(filter)())],
  },
  onCreate: async () => {
    const newId = await showCreateCompanyDialog();
    return newId ?? '';
  },
});
